/**
 * Kundenstimmen für die öffentliche Seite.
 *
 * Eigene Datei und nicht in `abfragen.ts`: Referenzen gehören nicht in den
 * Offlinespeicher. Man pflegt sie im Büro, nicht auf dem Platz.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api } from './klient'

export type Referenz = {
  id: string
  einrichtung: string
  ort: string | null
  text: string
  person: string | null
  funktion: string | null
  sortierung: number
  aktiv: boolean
  /** Wann der genannte Kunde der Veröffentlichung zugestimmt hat. */
  freigegeben_am: string | null
}

export type ReferenzEingabe = {
  einrichtung: string
  ort: string | null
  text: string
  person: string | null
  funktion: string | null
  aktiv: boolean
  freigegeben_am: string | null
  /** Nur beim Umsortieren mitgeschickt; beim Anlegen vergibt der Server sie. */
  sortierung?: number
}

const schluessel = ['referenzen'] as const

export function useReferenzen(rest?: { enabled?: boolean }) {
  return useQuery({
    queryKey: schluessel,
    queryFn: ({ signal }) => api.holen<Referenz[]>('/api/referenzen', signal),
    enabled: rest?.enabled ?? true,
  })
}

export function useReferenzAnlegen() {
  const speicher = useQueryClient()
  return useMutation({
    mutationFn: (daten: ReferenzEingabe) => api.senden<Referenz>('/api/referenzen', daten),
    onSuccess: () => speicher.invalidateQueries({ queryKey: schluessel }),
  })
}

export function useReferenzAendern() {
  const speicher = useQueryClient()
  return useMutation({
    mutationFn: ({ id, ...daten }: { id: string } & Partial<ReferenzEingabe>) =>
      api.aendern<Referenz>(`/api/referenzen/${id}`, daten),
    onSuccess: () => speicher.invalidateQueries({ queryKey: schluessel }),
  })
}

export function useReferenzLoeschen() {
  const speicher = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => api.loeschen(`/api/referenzen/${id}`),
    onSuccess: () => speicher.invalidateQueries({ queryKey: schluessel }),
  })
}
